import { Box, Flex, Link, Progress, Stack, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FrameworkAgreementDto, Currency } from "../../autogen/bff-api";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { urls } from "../../urls";
import { LabelValueGrid } from "../sourcing-events/portal/view-single/LabelValueGrid";
import { displaySum } from "../../common/formatting/displaySum";
import { calledOffPercent } from "./calledOffPercent";
import { EditableResponsiblePerson, ResponsiblePersonName } from "./ResponsiblePersonFact";

export const FrameworkAgreementFacts = ({
  framework,
  canEdit = false,
}: {
  framework: FrameworkAgreementDto;
  canEdit?: boolean;
}) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { summary } = framework;

  return (
    <LabelValueGrid
      columns={{ base: 1, sm: 2, lg: 3 }}
      rows={[
        { label: t("Suppliers"), value: String(summary.supplierCount) },
        { label: t("Mini-competitions"), value: String(summary.miniCompetitionCount) },
        { label: t("Contract period"), value: <ContractPeriod framework={framework} /> },
        {
          label: t("Responsible person"),
          value: canEdit ? (
            <EditableResponsiblePerson frameworkAgreementId={framework.id} />
          ) : (
            <ResponsiblePersonName person={framework.responsiblePerson} />
          ),
        },
        {
          label: t("Next supplier contract to end"),
          value: summary.nextPartyContractEndDate ? displayDate(summary.nextPartyContractEndDate) : "-",
        },
        framework.sourcingEventId
          ? {
              label: t("Awarded in"),
              value: (
                <Link as={RouterLink} to={urls.app.sourcingEvents.view.go(framework.sourcingEventId)} color="smPrimary">
                  {t("Go to the competition")}
                </Link>
              ),
            }
          : { label: t("Entered into"), value: t("Outside SourceMagnet") },
      ]}
    />
  );
};

const ContractPeriod = ({ framework }: { framework: Pick<FrameworkAgreementDto, "startDate" | "endDate"> }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { startDate, endDate } = framework;
  if (startDate && endDate) return <>{`${displayDate(startDate)} – ${displayDate(endDate)}`}</>;
  if (startDate) return <>{t("From {{date}}", { date: displayDate(startDate) })}</>;
  if (endDate) return <>{t("Until {{date}}", { date: displayDate(endDate) })}</>;
  return <>{t("Not specified")}</>;
};

export const CalledOffSoFar = ({ framework }: { framework: FrameworkAgreementDto }) => {
  const { t } = useTranslation();
  const currency = framework.ceilingValue?.currency ?? Currency.Nok;
  const ceiling = framework.ceilingValue?.value == null ? undefined : Number(framework.ceilingValue.value);
  const { calledOffValue, remainingCeilingValue } = framework.summary;
  const spendWithheld = ceiling !== undefined && remainingCeilingValue == null;

  if (spendWithheld && calledOffValue == null) {
    return (
      <SpendCard title={t("Called off so far")}>
        <CeilingOnly ceiling={displaySum({ value: ceiling, currency })} />
        <Text mt="2" fontSize="sm" color="smMuted">
          {t("You do not have access to the call-offs under this framework agreement.")}
        </Text>
      </SpendCard>
    );
  }

  if (spendWithheld) {
    return (
      <SpendCard title={t("Call-offs you can see")}>
        <Text mt="2" fontSize="2xl" fontWeight="bold" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {displaySum({ value: Number(calledOffValue), currency })}
        </Text>
        <CeilingOnly ceiling={displaySum({ value: ceiling, currency })} />
        <Text mt="2" fontSize="sm" color="smMuted">
          {t("You do not have access to every call-off, so how much of the ceiling value is used is not shown.")}
        </Text>
      </SpendCard>
    );
  }

  if (ceiling === undefined && calledOffValue == null) {
    return (
      <SpendCard title={t("Called off so far")}>
        <Text mt="2" fontSize="sm" color="smMuted">
          {t("No call-offs to show")}
        </Text>
      </SpendCard>
    );
  }

  const spent =
    ceiling !== undefined && remainingCeilingValue != null
      ? ceiling - Number(remainingCeilingValue)
      : Number(calledOffValue ?? 0);
  const share = ceiling !== undefined && ceiling > 0 ? calledOffPercent(spent, ceiling) : undefined;
  const isOverCeiling = share !== undefined && share > 100;

  return (
    <SpendCard
      title={t("Called off so far")}
      note={
        share !== undefined && (
          <Text fontSize="sm" color={isOverCeiling ? "smError" : "smMuted"}>
            {t("{{percent}} % of the ceiling value used", { percent: share })}
          </Text>
        )
      }
    >
      <Stack direction="row" align="baseline" spacing="2" mt="2">
        <Text fontSize="2xl" fontWeight="bold" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {displaySum({ value: spent, currency })}
        </Text>
        {ceiling !== undefined && (
          <Text fontSize="md" color="smMuted" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {t("of {{ceiling}}", { ceiling: displaySum({ value: ceiling, currency }) })}
          </Text>
        )}
      </Stack>

      {share !== undefined && (
        <Progress
          mt="4"
          size="sm"
          rounded="full"
          bg="smBackgroundTertiary"
          aria-label={String(t("Progress"))}
          value={Math.min(share, 100)}
          colorScheme={isOverCeiling ? "red" : "teal"}
          sx={{ "& > div": { borderRadius: "full" } }}
        />
      )}
    </SpendCard>
  );
};

const SpendCard = ({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) => (
  <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
    <Flex justify="space-between" align="baseline" gap="4" flexWrap="wrap">
      <Text color="smMuted" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
        {title}
      </Text>
      {note}
    </Flex>
    {children}
  </Box>
);

const CeilingOnly = ({ ceiling }: { ceiling: string }) => {
  const { t } = useTranslation();
  return (
    <Text mt="2" fontSize="md" color="smMuted" sx={{ fontVariantNumeric: "tabular-nums" }}>
      {t("Ceiling value: {{ceiling}}", { ceiling })}
    </Text>
  );
};
